import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { BookOpen, Sparkles, ChevronRight, Star, Zap, Gift, Trophy, Target, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import SEOHead from './SEOHead';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FreeTrialExams = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [examBanks, setExamBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchExamBanks();
  }, []);

  const handleStartTrial = (examId) => {
    if (!user) {
      toast.info('Please sign in to start your free trial');
      openAuth('login');
      return;
    }
    navigate(`/exam-bank/${examId}?trial=true`);
  };

  const fetchExamBanks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exam-bank/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const trialExams = response.data.exam_banks;
        
        // Custom sorting: IIT-JEE, NEET-UG, GRE, GMAT first, then rest by display_order
        const priorityOrder = ['iit-jee', 'neet-ug', 'gre', 'gmat'];
        
        trialExams.sort((a, b) => {
          const aIndex = priorityOrder.indexOf(a.exam_id);
          const bIndex = priorityOrder.indexOf(b.exam_id);
          
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return (a.display_order || 999) - (b.display_order || 999);
        });
        
        setExamBanks(trialExams);
      }
    } catch (error) {
      console.error('Error fetching exam banks:', error);
      toast.error('Failed to load exam banks');
    } finally {
      setLoading(false);
    }
  };

  const getCategory = (examId) => {
    if (['plab1', 'usmle-step1', 'usmle-step2ck', 'usmle-step3', 'amc-mcq', 'dha-mcq', 'mccqe-parti'].includes(examId)) {
      return 'medical';
    }
    if (['dha-dental', 'adc-part1', 'ore-part1', 'inbde'].includes(examId)) {
      return 'dental';
    }
    if (examId.includes('naplex') || examId.includes('mpje') || examId.includes('pebc') || 
        examId.includes('gphc') || examId.includes('opra') || examId.includes('cap') || 
        examId.includes('dha-pharma') || examId.includes('sple')) {
      return 'pharmacy';
    }
    if (['iit-jee', 'neet-ug'].includes(examId)) {
      return 'engineering';
    }
    if (['gre', 'gmat'].includes(examId)) {
      return 'global';
    }
    return 'other';
  };

  const getCategoryStyle = (examId) => {
    const category = getCategory(examId);
    const styles = {
      medical: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: '👨‍⚕️', gradient: 'from-emerald-500 to-teal-600' },
      dental: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: '🦷', gradient: 'from-cyan-500 to-blue-600' },
      pharmacy: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: '💊', gradient: 'from-teal-500 to-green-600' },
      engineering: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: '🚀', gradient: 'from-blue-700 to-blue-800' },
      global: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: '🌍', gradient: 'from-green-500 to-emerald-600' },
      other: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: '📚', gradient: 'from-purple-500 to-pink-600' }
    };
    return styles[category] || styles.other;
  };

  const categories = [
    { id: 'all', label: 'All Exams', icon: BookOpen },
    { id: 'engineering', label: 'IIT-JEE & NEET-UG', icon: Zap },
    { id: 'global', label: 'Global', icon: GraduationCap },
    { id: 'medical', label: 'Medical', icon: Target },
    { id: 'dental', label: 'Dental', icon: Star },
    { id: 'pharmacy', label: 'Pharmacy', icon: Gift },
  ];

  const filteredExams = selectedCategory === 'all' 
    ? examBanks 
    : examBanks.filter(exam => getCategory(exam.exam_id) === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading free trial exams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Free Trial Exams - PLAB, USMLE, AMC, DHA, NAPLEX"
        description="Practice top international medical and licensing exams for free on Nerd Nation. Try PLAB, USMLE, AMC, DHA, ADC, ORE, NAPLEX, PEBC and more."
        path="/free-trial-exams"
      />
      {/* Hero Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-amber-100 rounded-full mb-4 sm:mb-6">
              <Gift className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              <span className="text-xs sm:text-sm font-bold text-amber-700">100% FREE - NO CREDIT CARD REQUIRED</span>
            </div>
            
            {/* Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
              Try All Exams Free!
            </h1>
            
            {/* Subtitle */}
            <p className="text-sm sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto mb-5 sm:mb-8 px-2">
              Experience 10 curated questions from each exam with full Ask Nerd AI support before you subscribe
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">10 Questions/Exam</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Star className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">Ask Nerd AI Included</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Trophy className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">Detailed Explanations</span>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xs sm:max-w-lg mx-auto">
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{examBanks.length}</p>
                <p className="text-xs sm:text-sm text-gray-500">Exams</p>
              </div>
              <div className="text-center border-x border-gray-200">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">10</p>
                <p className="text-xs sm:text-sm text-gray-500">Questions Each</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-amber-500">FREE</p>
                <p className="text-xs sm:text-sm text-gray-500">Forever</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-gray-900 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Exam Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        {filteredExams.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base sm:text-lg">No exams found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredExams.map((bank) => {
              const style = getCategoryStyle(bank.exam_id);
              return (
                <Card
                  key={bank.exam_id}
                  className={`group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${style.border} overflow-hidden`}
                  onClick={() => handleStartTrial(bank.exam_id)}
                >
                  {/* Card Header with gradient */}
                  <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${style.gradient}`}></div>
                  
                  <CardContent className="p-4 sm:p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`w-10 h-10 sm:w-14 sm:h-14 ${style.bg} rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0`}>
                        {style.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-lg mb-0.5 sm:mb-1 truncate">{bank.display_name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{bank.description}</p>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className={`${style.bg} rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className={`text-xl sm:text-2xl font-bold ${style.text}`}>10</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">Free Questions</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs sm:text-sm font-semibold text-gray-700">{bank.total_questions}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">Total in Bank</p>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Button 
                      className={`w-full bg-gradient-to-r ${style.gradient} hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base group-hover:shadow-lg transition-all`}
                    >
                      <span>Start Free Trial</span>
                      <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    
                    <p className="text-[10px] sm:text-xs text-center text-gray-400 mt-2 sm:mt-3">
                      Sign in to start • Free forever
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-20 sm:mb-24"></div>
      <AdvisorRibbon />
      <Footer />
    </div>
  );
};

export default FreeTrialExams;
